import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LayoutDashboard, Save, Move, MoveHorizontal, MoveVertical, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "@/components/ImagePicker";
import SoundPicker from "@/components/SoundPicker";
import { OverlayMock } from "@/components/OverlayMock";

const ELEMENTS = [
  { key: "now_playing", label: "🎵 Now Playing", color: "#FF3366" },
  { key: "stash", label: "🫙 Stash (jar + tray)", color: "#f0b429" },
  { key: "event_feed", label: "🔔 Alerts feed", color: "#38bdf8" },
  { key: "slots", label: "🎰 Slots popup", color: "#a855f7" },
  { key: "mc_gift", label: "🎁 Minecraft gift alert", color: "#22c55e" },
  { key: "avatar", label: "🧍 3D Avatar", color: "#34d399" },
];

export default function OverlayLayoutEditor() {
  const [layout, setLayout] = useState(null);
  const [stash, setStash] = useState({ cookies: 0, cupcakes: 0 });
  const [sel, setSel] = useState("stash");
  const [saving, setSaving] = useState(false);
  const [snap, setSnap] = useState(true);
  const [guides, setGuides] = useState({ v: null, h: null });
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const canvasRef = useRef(null);
  const [canvasW, setCanvasW] = useState(0);
  useEffect(() => {
    const el = canvasRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setCanvasW(el.clientWidth)); ro.observe(el); setCanvasW(el.clientWidth);
    return () => ro.disconnect();
  }, [!!layout]); // eslint-disable-line react-hooks/exhaustive-deps
  const drag = useRef(null);
  const layoutStateRef = useRef(null);
  layoutStateRef.current = layout;

  const clone = (o) => JSON.parse(JSON.stringify(o));
  const snapshot = () => {
    if (!layoutStateRef.current) return;
    setHistory((h) => [...h.slice(-49), clone(layoutStateRef.current)]);
    setFuture([]);
  };
  const undo = () => {
    if (!history.length) return;
    setFuture((f) => [...f.slice(-49), clone(layout)]);
    setLayout(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
  };
  const redo = () => {
    if (!future.length) return;
    setHistory((h) => [...h.slice(-49), clone(layout)]);
    setLayout(future[future.length - 1]);
    setFuture((f) => f.slice(0, -1));
  };

  const GRID = 2.5;
  const SNAP_TARGETS = [0, 25, 50, 75, 95];
  const snapAxis = (val) => {
    for (const t of SNAP_TARGETS) if (Math.abs(val - t) < 1.6) return { v: t, guide: t };
    return { v: Math.round(val / GRID) * GRID, guide: null };
  };

  useEffect(() => {
    api.get("/overlay/layout").then((r) => setLayout(r.data)).catch(() => {});
    api.get("/stash").then((r) => setStash(r.data)).catch(() => {});
  }, []);

  const setEl = (key, patch) => setLayout((l) => ({ ...l, [key]: { ...l[key], ...patch } }));
  const setField = (key, val) => setLayout((l) => ({ ...l, [key]: val }));

  const onPointerDown = (e, key) => {
    e.preventDefault();
    setSel(key);
    snapshot();
    drag.current = key;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const onMove = (e) => {
    const key = drag.current;
    const c = canvasRef.current;
    if (!key || !c) return;
    const r = c.getBoundingClientRect();
    let x = ((e.clientX - r.left) / r.width) * 100;
    let y = ((e.clientY - r.top) / r.height) * 100;
    x = Math.max(0, Math.min(95, x));
    y = Math.max(0, Math.min(95, y));
    let gv = null, gh = null;
    if (snap) {
      const sx = snapAxis(x), sy = snapAxis(y);
      x = sx.v; y = sy.v; gv = sx.guide; gh = sy.guide;
    }
    setGuides({ v: gv, h: gh });
    setLayout((l) => ({ ...l, [key]: { ...l[key], x: +x.toFixed(1), y: +y.toFixed(1) } }));
  };
  const onUp = () => {
    drag.current = null;
    setGuides({ v: null, h: null });
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };

  // arrow-key nudge of the selected element (Shift = bigger step)
  useEffect(() => {
    const onKey = (e) => {
      if (!sel) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const step = e.shiftKey ? 2 : 0.5;
      let dx = 0, dy = 0;
      if (e.key === "ArrowLeft") dx = -step;
      else if (e.key === "ArrowRight") dx = step;
      else if (e.key === "ArrowUp") dy = -step;
      else if (e.key === "ArrowDown") dy = step;
      else return;
      e.preventDefault();
      snapshot();
      setLayout((l) => ({ ...l, [sel]: {
        ...l[sel],
        x: +Math.max(0, Math.min(95, (l[sel].x || 0) + dx)).toFixed(1),
        y: +Math.max(0, Math.min(95, (l[sel].y || 0) + dy)).toFixed(1),
      } }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/overlay/layout", layout);
      setLayout(r.data);
      toast.success("Overlay layout saved — your OBS overlay will update in a few seconds");
    } catch (e) { toast.error("Couldn't save layout"); }
    finally { setSaving(false); }
  };

  if (!layout) return <div className="text-sm text-muted-foreground" data-testid="overlay-editor-loading">Loading your overlay layout…</div>;

  const selEl = ELEMENTS.find((e) => e.key === sel);
  const jar = layout.stash_jar_img;
  const tray = layout.stash_tray_img;

  return (
    <div className="space-y-6" data-testid="overlay-editor-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-800 font-heading flex items-center gap-2"><LayoutDashboard className="w-7 h-7 text-primary" /> Overlay Editor</h1>
          <p className="text-muted-foreground mt-1 text-sm">Drag the boxes to arrange your OBS overlay. Changes save to your live overlay.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={undo} disabled={!history.length} variant="outline" className="border-white/10 rounded-lg" data-testid="overlay-editor-undo"><Undo2 className="w-4 h-4 mr-2" />Undo</Button>
          <Button onClick={redo} disabled={!future.length} variant="outline" className="border-white/10 rounded-lg" data-testid="overlay-editor-redo"><Redo2 className="w-4 h-4 mr-2" />Redo</Button>
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 rounded-lg" data-testid="overlay-editor-save"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save layout"}</Button>
        </div>
      </header>

      {/* Preview canvas */}
      <div>
        <div className="text-xs text-muted-foreground mb-2 flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-1"><Move className="w-3.5 h-3.5" /> Drag a box, or click it and nudge with arrow keys (Shift = bigger steps).</span>
          <label className="flex items-center gap-2 text-white cursor-pointer" data-testid="overlay-snap-toggle-wrap">
            <span>Snap to grid & guides</span>
            <Switch checked={snap} onCheckedChange={setSnap} data-testid="overlay-snap-toggle" />
          </label>
        </div>
        <div ref={canvasRef} data-testid="overlay-canvas"
             className="relative w-full rounded-xl overflow-hidden select-none"
             style={{ aspectRatio: "16 / 9", background: "repeating-linear-gradient(45deg,#0d0d0f,#0d0d0f 18px,#101013 18px,#101013 36px)", border: "1px solid rgba(255,255,255,0.12)" }}>
          {snap && (
            <div className="absolute inset-0 pointer-events-none" data-testid="overlay-grid"
                 style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.05) 1px,transparent 1px)", backgroundSize: "10% 10%" }} />
          )}
          {/* live edge/center guides while dragging */}
          {guides.v != null && <div className="absolute top-0 bottom-0 pointer-events-none" data-testid="overlay-guide-v" style={{ left: `${guides.v}%`, width: 1, background: "#38bdf8", boxShadow: "0 0 8px #38bdf8" }} />}
          {guides.h != null && <div className="absolute left-0 right-0 pointer-events-none" data-testid="overlay-guide-h" style={{ top: `${guides.h}%`, height: 1, background: "#38bdf8", boxShadow: "0 0 8px #38bdf8" }} />}
          {/* RPG battle line (fixed position on the overlay) — shown so nothing gets parked on top of the fighters */}
          {layout.rpg?.visible !== false && (
            <div className="absolute left-0 right-0 pointer-events-none" data-testid="overlay-rpg-stage"
                 style={{ bottom: `${(118 / 1080) * 100}%`, height: `${(130 / 1080) * 100}%`, borderTop: "1.5px dashed rgba(251,191,36,0.6)", borderBottom: "1.5px dashed rgba(251,191,36,0.35)", background: "rgba(251,191,36,0.05)" }}>
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-700 px-1.5 rounded-t whitespace-nowrap" style={{ background: "rgba(10,10,10,0.8)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.6)", borderBottom: "none" }}>⚔️ RPG battle line (fixed)</div>
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between px-[8%]" style={{ fontSize: `${Math.max(14, ((canvasW || 960) / 1920) * 56)}px`, lineHeight: 1 }}>
                <span>🐉</span><span>👾</span><span>🧟</span><span className="ml-auto">🧙‍♀️🛡️🏹</span>
              </div>
            </div>
          )}
          {ELEMENTS.map((el) => {
            const e = layout[el.key];
            if (!e) return null;
            const dim = e.visible === false ? 0.3 : 1;
            const k = (canvasW || 960) / 1920;
            return (
              <div key={el.key}
                   data-testid={`overlay-box-${el.key}`}
                   onPointerDown={(ev) => onPointerDown(ev, el.key)}
                   className="absolute cursor-grab active:cursor-grabbing"
                   style={{ left: `${e.x}%`, top: `${e.y}%`, opacity: dim, zIndex: sel === el.key ? 10 : 5 }}>
                <div className="absolute -top-4 left-0 text-[10px] font-700 px-1.5 rounded-t whitespace-nowrap"
                     style={{ background: sel === el.key ? el.color : "rgba(10,10,10,0.8)", color: sel === el.key ? "#0a0a0a" : el.color, border: `1px solid ${el.color}`, borderBottom: "none" }}>
                  {el.label}
                </div>
                <div className="rounded-lg" style={{ outline: `1.5px ${sel === el.key ? "solid" : "dashed"} ${el.color}`, boxShadow: sel === el.key ? `0 0 16px ${el.color}` : "none" }}>
                  <div className="pointer-events-none" style={{ zoom: k * (e.scale ?? 1), width: "max-content" }}>
                    <OverlayMock kind={el.key} layout={layout} />
                  </div>
                </div>
              </div>
            );
          })}
          <div className="absolute bottom-1 right-2 text-[9px] text-white/30">preview</div>
        </div>
      </div>

      {/* Selected element controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5 space-y-4" data-testid="overlay-element-controls">
          <div className="flex items-center gap-2 flex-wrap">
            {ELEMENTS.map((el) => (
              <button key={el.key} onClick={() => setSel(el.key)}
                      data-testid={`overlay-select-${el.key}`}
                      className="text-xs px-3 py-1.5 rounded-full font-600 transition-colors"
                      style={{ background: sel === el.key ? el.color : "rgba(255,255,255,0.06)", color: sel === el.key ? "#0a0a0a" : "#fff" }}>
                {el.label}
              </button>
            ))}
          </div>
          <div>
            <div className="flex justify-between text-xs mb-2"><span className="text-muted-foreground">Size ({selEl.label})</span><span className="text-primary">{Math.round((layout[sel].scale ?? 1) * 100)}%</span></div>
            <div onPointerDownCapture={snapshot}><Slider data-testid="overlay-scale" value={[layout[sel].scale ?? 1]} min={0.4} max={2.5} step={0.05} onValueChange={(v) => setEl(sel, { scale: v[0] })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px] text-muted-foreground">X position %</Label><Input type="number" value={layout[sel].x} onFocus={snapshot} onChange={(e) => setEl(sel, { x: Math.max(0, Math.min(95, parseFloat(e.target.value) || 0)) })} className="bg-secondary border-white/10 h-9 mt-1" data-testid="overlay-x" /></div>
            <div><Label className="text-[11px] text-muted-foreground">Y position %</Label><Input type="number" value={layout[sel].y} onFocus={snapshot} onChange={(e) => setEl(sel, { y: Math.max(0, Math.min(95, parseFloat(e.target.value) || 0)) })} className="bg-secondary border-white/10 h-9 mt-1" data-testid="overlay-y" /></div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-white/10 flex-1" onClick={() => { snapshot(); setEl(sel, { x: 50 }); }} data-testid="overlay-center-h"><MoveHorizontal className="w-4 h-4 mr-1" />Center ↔</Button>
            <Button variant="outline" size="sm" className="border-white/10 flex-1" onClick={() => { snapshot(); setEl(sel, { y: 50 }); }} data-testid="overlay-center-v"><MoveVertical className="w-4 h-4 mr-1" />Center ↕</Button>
            <Button variant="outline" size="sm" className="border-white/10 flex-1" onClick={() => { snapshot(); setEl(sel, { x: 50, y: 50 }); }} data-testid="overlay-center-both">Middle</Button>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
            <span className="text-sm">Show {selEl.label} on overlay</span>
            <Switch checked={layout[sel].visible !== false} onCheckedChange={(v) => { snapshot(); setEl(sel, { visible: v }); }} data-testid="overlay-visible" />
          </div>
        </div>

        {/* Show/hide only + stash art */}
        <div className="space-y-6">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5" data-testid="overlay-toggles">
            <h3 className="font-heading font-700 text-sm mb-3">Show / hide big animations</h3>
            {[["rpg", "⚔️ RPG Idle-Battler"], ["fishing", "🎣 Fishing animation"]].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3 mb-2">
                <span className="text-sm">{label}</span>
                <Switch checked={layout[k]?.visible !== false} onCheckedChange={(v) => { snapshot(); setField(k, { visible: v }); }} data-testid={`overlay-toggle-${k}`} />
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground mt-1">These stay in their spot but you can turn them on or off.</p>
          </div>

          <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5" data-testid="overlay-stash-art">
            <h3 className="font-heading font-700 text-sm mb-1">🫙 Stash look (jar & tray)</h3>
            <p className="text-[11px] text-muted-foreground mb-4">Uses the emoji unless you add artwork. Current stash: 🍪 {stash.cookies} · 🧁 {stash.cupcakes}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <div className="text-xs font-700 text-cookie">Cookie Jar</div>
                <div className="flex gap-2">
                  <div className="w-16"><Label className="text-[10px] text-muted-foreground">Emoji</Label><Input value={layout.stash_jar_emoji || ""} onChange={(e) => setField("stash_jar_emoji", e.target.value)} className="bg-secondary border-white/10 h-9 mt-1 text-center" data-testid="stash-jar-emoji" /></div>
                  <div className="flex-1"><Label className="text-[10px] text-muted-foreground">Label</Label><Input value={layout.stash_jar_label || ""} onChange={(e) => setField("stash_jar_label", e.target.value)} className="bg-secondary border-white/10 h-9 mt-1" data-testid="stash-jar-label" /></div>
                </div>
                <div><div className="text-[10px] text-muted-foreground mb-1">Artwork (optional)</div><ImagePicker value={jar || ""} onChange={(v) => setField("stash_jar_img", v)} testid="stash-jar-img" /></div>
              </div>
              <div className="space-y-2">
                <div className="text-xs font-700 text-primary">Cupcake Tray</div>
                <div className="flex gap-2">
                  <div className="w-16"><Label className="text-[10px] text-muted-foreground">Emoji</Label><Input value={layout.stash_tray_emoji || ""} onChange={(e) => setField("stash_tray_emoji", e.target.value)} className="bg-secondary border-white/10 h-9 mt-1 text-center" data-testid="stash-tray-emoji" /></div>
                  <div className="flex-1"><Label className="text-[10px] text-muted-foreground">Label</Label><Input value={layout.stash_tray_label || ""} onChange={(e) => setField("stash_tray_label", e.target.value)} className="bg-secondary border-white/10 h-9 mt-1" data-testid="stash-tray-label" /></div>
                </div>
                <div><div className="text-[10px] text-muted-foreground mb-1">Artwork (optional)</div><ImagePicker value={tray || ""} onChange={(v) => setField("stash_tray_img", v)} testid="stash-tray-img" /></div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
              <Label className="text-[11px] text-muted-foreground">🔊 Stash pop sound (plays when the jar+tray appear — leave empty for the default chime)</Label>
              <div className="mt-1"><SoundPicker value={layout.stash_sound_url || ""} onChange={(v) => setField("stash_sound_url", v)} testid="stash-sound" /></div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">The jar + tray pop up on the overlay for ~5 seconds whenever the stash changes (a gift, bribe, or sneak).</p>
          </div>
        </div>
      </div>
    </div>
  );
}
